import type { KeyboardEvent, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { t } from '@/messages/zh-TW';

/**
 * 第二層版型：對話框外框（header／body／footer + 遮罩）。
 *
 * 原系統對應：
 *   - Bootstrap 3 modal：`layout/navbar.jsp:107-181`
 *     `<div class="modal fade" tabindex="-1" role="dialog" aria-labelledby="myModalLabel" aria-hidden="true">`
 *     `<div class="modal-dialog modal-lg"><div class="modal-content">`
 *     `<div class="modal-header"><button class="close" data-dismiss="modal">&times;</button><h3 class="modal-title">…`
 *     `<div class="modal-body">…`
 *   - jquery-confirm（`js-my/common.js:317-379` 的四個共用對話框）：
 *     `closeIcon`（右上角 ×）、`type: 'blue'|'red'`（左緣色條）、`icon: 'fa fa-info-circle'`
 *
 * ── 為什麼不包第一層的 <Modal> ─────────────────────────────────────────────
 * 本次要求對話框必須有 focus trap、Esc 關閉與 aria-*。原生 `<dialog>.showModal()` 本來可以
 * 免費拿到這些，但**實測 jsdom 30 沒有實作 showModal**（`typeof dialog.showModal === 'undefined'`），
 * 在 Vitest 環境下完全無法驗證，所以只能自己實作焦點管理。
 * 焦點行為必須跟外框長在一起（外面包不了裡面的 Tab 迴圈），因此 ModalFrame 自帶實作，
 * 不轉包給第一層 `<Modal>` —— 第一層自己的註解也寫明「沒有 focus trap、沒有開啟時自動聚焦」
 * 且「ModalFrame（標題＋內容＋確定取消按鈕的固定組合）是第二層的事」
 * （`src/components/base/Modal.tsx` 的 ponytail 取捨區塊）。
 * 另外三個原系統行為第一層也沒有對應 prop：`dismissible=false`（sysConfirm 不給關）、
 * `tone`（jquery-confirm 的 red／blue）、`size`（modal-lg）。
 * **這是刻意偏離契約，需與第一層負責人收斂**：建議最後讓 `base/Modal` 改成
 * ModalFrame 的薄包裝，或直接移除，避免全站有兩套 modal。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export type ModalTone = 'default' | 'info' | 'danger';

export interface ModalFrameProps {
  uiId: string;
  open: boolean;
  title: string;
  /** 使用者要求關閉（×、Esc、點遮罩）時呼叫。 */
  onClose(): void;
  /**
   * 是否可由使用者關閉。對應 jquery-confirm 的 `closeIcon`：
   * `showInfoDialog`/`showAlertDialog` 是 true（common.js:335,347），
   * `sysConfirm` 是 false（common.js:359）。false 時不畫 ×、Esc 與點遮罩皆無效。
   */
  dismissible?: boolean;
  /** jquery-confirm 的 `type`：info=blue（common.js:333）、danger=red（:345,362）。 */
  tone?: ModalTone;
  /** `modal-lg`（navbar.jsp:108）。 */
  size?: 'md' | 'lg';
  footer?: ReactNode;
  children?: ReactNode;
}

// ponytail: 靜態對照表，不動態組 class（§8.1）。
const TONE_BAR = {
  default: 'border-t-border',
  info: 'border-t-info',
  danger: 'border-t-danger',
} as const;

const SIZE = {
  md: 'max-w-[600px]',
  lg: 'max-w-[900px]',
} as const;

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ModalFrame({
  uiId,
  open,
  title,
  onClose,
  dismissible = true,
  tone = 'default',
  size = 'md',
  footer,
  children,
}: ModalFrameProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // 開啟時把焦點移進對話框，關閉時還回原本的元素。
  useEffect(() => {
    if (!open) return;
    restoreRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    return () => {
      restoreRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      if (dismissible) {
        event.stopPropagation();
        onClose();
      }
      return;
    }
    if (event.key !== 'Tab') return;

    // focus trap：Tab 在對話框內循環，不會跑到背景頁面。
    const panel = panelRef.current;
    if (panel === null) return;
    const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    // 遮罩是純視覺層，點它等同「取消」。鍵盤使用者的等效逃生口是對話框上的 Esc
    // （見 handleKeyDown），不需要遮罩自己可聚焦 —— 讓遮罩進入 Tab 順序反而會多一個沒有語意的停留點。
    // biome-ignore lint/a11y/noStaticElementInteractions: 見上方說明，鍵盤等效操作由 Esc 提供
    <div
      data-ui-id={`${uiId}-backdrop`}
      // FC-5 E-Q1 裁決：`ProcessingOverlay` 改成 `z-[3000]`，疊在對話框之上（對齊原系統
      // `showProcessing()` 語意，推翻 FC-4 C7，理由見 `ProcessingOverlay.tsx` 檔頭）。
      // 本層與 `base/Modal.tsx:61` 及 `LegacyContractModal.tsx` 同一個值，全站只有一套疊放規則。
      className="fixed inset-0 z-[2050] flex items-start justify-center overflow-y-auto bg-black/50 p-6"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        data-ui-id={uiId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uiId}-title`}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`w-full border-t-[3px] bg-surface shadow-lg ${TONE_BAR[tone]} ${SIZE[size]}`}
      >
        <div
          data-ui-id={`${uiId}-header`}
          className="flex items-start gap-3 border-border border-b px-4 py-3"
        >
          <h2
            id={`${uiId}-title`}
            data-ui-id={`${uiId}-title`}
            className="flex-1 font-bold text-[18px]"
          >
            {title}
          </h2>
          {dismissible ? (
            <button
              type="button"
              data-ui-id={`${uiId}-close`}
              aria-label={t('action.close')}
              onClick={onClose}
              className="text-[30px] leading-none"
            >
              ×
            </button>
          ) : null}
        </div>
        {children === undefined ? null : (
          <div data-ui-id={`${uiId}-body`} className="px-4 py-4">
            {children}
          </div>
        )}
        {footer === undefined ? null : (
          <div
            data-ui-id={`${uiId}-footer`}
            className="flex flex-wrap justify-end gap-2 border-border border-t px-4 py-3"
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
